import { AbstractService } from '@/services/core/serviceFactory';
import { usersGraphqlService, type HBoxUser as APIHBoxUser } from '@/services/api';
import type { GraphQLClientResponse } from '@/services/core/graphqlClient';
import { USERS_QUERIES } from '@/services/resources/usersGquery';

export interface HBoxUser {
  id: number;
  first_name: string;
  last_name: string;
  type: string;
}

export interface UserDataResponse {
  api_hboxuser: APIHBoxUser[];
}

export interface UserAuditLog {
  metadata: any;
  changed_by: string | null;
  changed_at: string;
  action: string;
}

export class UserDataService extends AbstractService {
  /**
   * Fetch HBox users by type
   */
  async fetchHBoxUsers(): Promise<GraphQLClientResponse<UserDataResponse>> {
    this.requireAuth();

    try {
      return await usersGraphqlService.getHBoxUsers(
        this.context.hasuraToken
      ) as GraphQLClientResponse<UserDataResponse>;
    } catch (error) {
      console.error('Error fetching HBox users:', error);
      throw new Error('Failed to fetch HBox users');
    }
  }

  /**
   * Fetch HBox users by a list of IDs
   */
  async fetchHBoxUsersByIds(ids: number[]): Promise<GraphQLClientResponse<UserDataResponse>> {
    this.requireAuth();

    try {
      return await usersGraphqlService.getHBoxUsersByIds(
        this.context.hasuraToken!,
        ids
      ) as GraphQLClientResponse<UserDataResponse>;
    } catch (error) {
      console.error('Error fetching HBox users by IDs:', error);
      throw new Error('Failed to fetch HBox users by IDs');
    }
  }

  /**
   * Process users into a map of ID to full name
   */
  processUsersToMap(users: HBoxUser[]): Record<number, string> {
    const userMap: Record<number, string> = {};
    
    users.forEach(user => {
      userMap[user.id] = `${user.first_name} ${user.last_name}`.trim();
    });
    
    return userMap;
  }

  /**
   * Fetch user audit log
   */
  async getUserAuditLog(userId: number): Promise<{
    logs: UserAuditLog[];
    total: number;
  }> {
    this.requireAuth();

    try {
      const result = await this.graphqlClient.query<{
        pep_user_details_audit_log: UserAuditLog[];
        pep_user_details_audit_log_aggregate: { aggregate: { count: number } };
      }>(
        USERS_QUERIES.GET_USER_DETAILS_AUDIT_LOG,
        { userId },
        this.context.hasuraToken
      );

      return {
        logs: result.data?.pep_user_details_audit_log || [],
        total: result.data?.pep_user_details_audit_log_aggregate?.aggregate?.count || 0,
      };
    } catch (error) {
      console.error('Error fetching user audit log:', error);
      throw error;
    }
  }
}